import { EligibilityConditionCode } from "@lyfegen-platform/graphql-schema/models";
import { CountryName, distinct, isFixedPrice, sortBySelectors } from "@lyfegen-platform/utils";
import {
	GetAgreementDetails_baseAgreements,
	GetAgreementDetails_baseAgreements_currency,
	GetAgreementDetails_baseAgreements_dataContainers,
	GetAgreementDetails_baseAgreements_enrollingConditionSets,
	GetAgreementDetails_baseAgreements_scenarios_actions,
} from "../containers/agreements/agreement/types/GetAgreementDetails";
import { ConditionArea } from "../containers/cases/details/RecordsCard";
import { GetCaseByIdQuery } from "../containers/cases/types/queries.generated";
import { GetRefundSourceConditionsQuery } from "../containers/financials/refunds/types/queries.generated";
import {
	ActionItemCombinationRule,
	ActionItemRangeType,
	AgreementScope,
	AgreementSubjectMatter,
	AmountOfThresholdBasis,
	Article71Indication,
	BaseAgreement,
	Brand,
	ConditionType,
	DataContainer,
	Disease,
	EnrollingConditionSet,
	EnrollingConditionSetScopesRelationship,
	EntityPriceType,
	Generic,
	Indication,
	MonitoringType,
	Preparation,
	ScenarioAction,
	ScenarioCondition,
	TimeframeRangeType,
	TimeframeRelation,
	TimeframeStartingPoint,
	Treatment,
	TreatmentDurationConditionAspect,
	UoMType,
} from "../models/generated/graphql";
import formatNumber from "./format-number";
import { isEmptyValue } from "./is-empty-value";
import { selectMany } from "./select-many";
import { TrFun } from "./types";

export interface IFormattedGroupDict {
	formattedCondition: string;
	formattedActions: Array<string>;
	groupId: number;
	conditionTimeframeName: string;
}

type BaseAgreementDataType =
	| GetCaseByIdQuery["cases"][number]["baseAgreement"]
	| GetRefundSourceConditionsQuery["baseAgreements"][number]
	| GetAgreementDetails_baseAgreements;

export const notRequiredKey = "not-required";
export const notAvailableKey = "not-available";
export const customIndications = "custom-indications";
export const volumeBasedConditions = [ConditionType.ActiveIngredientQuantity, ConditionType.AmountOf, ConditionType.PrescriptionNumber];
export const conditionBoundRanges = [ActionItemRangeType.WithinConditionBound, ActionItemRangeType.StartingFromConditionBound];

export function formatDiseases(conditionSets: Array<EnrollingConditionSet>, t: TrFun) {
	const lastElements = [t(notAvailableKey), t(notRequiredKey)];
	return distinct(
		conditionSets.flatMap((conditionSet) =>
			(conditionSet.scopes || conditionSet.scopesConnection.edges.map((edge) => edge.node)).flatMap((scope) =>
				formatDiseaseByConditionSet(conditionSet, scope, t)
			)
		)
	).sort(
		sortBySelectors(
			(x) => lastElements.includes(x),
			(x) => x
		)
	);
}

export function formatDiseaseByConditionSet(conditionSet: EnrollingConditionSet, scope: AgreementScope, t: TrFun): Array<string> {
	if (conditionSet.isDiseaseNotEnrollingCondition) {
		return [t(notRequiredKey)];
	}

	if (!scope.diseases.length) {
		return [t(notAvailableKey)];
	}

	return scope.diseases.map((disease) => formatDisease(disease));
}

export function formatIndications(conditionSets: Array<EnrollingConditionSet>, t: TrFun) {
	return distinct(
		conditionSets.flatMap((conditionSet) =>
			(conditionSet.scopes || conditionSet.scopesConnection.edges.map((edge) => edge.node)).flatMap((scope) =>
				formatIndicationByConditionSet(conditionSet, scope, t)
			)
		)
	).sort(
		sortBySelectors(
			(x) => x === t(notRequiredKey) || x === t(notAvailableKey),
			(x) => x
		)
	);
}

export function formatIndicationByConditionSet(conditionSet: EnrollingConditionSet, scope: AgreementScope, t: TrFun): Array<string> {
	if (conditionSet.isIndicationNotEnrollingCondition) {
		return [t(notRequiredKey)];
	}

	if (!scope.indications.length) {
		return [t(notAvailableKey)];
	}

	return scope.indications.map((indication) => indicationDisplay(indication, t) as string);
}

export function getPreparations(
	agreement:
		| GetCaseByIdQuery["cases"][number]["baseAgreement"]
		| GetRefundSourceConditionsQuery["baseAgreements"][number]
		| NonNullable<GetCaseByIdQuery["cases"][number]["baseAgreement"]>
		| GetAgreementDetails_baseAgreements
): Array<Preparation> {
	return selectMany(
		(
			(agreement as GetAgreementDetails_baseAgreements).enrollingConditionSets
				.flatMap((conditionSet) => conditionSet.scopesConnection.edges)
				.map((edge) => edge.node) ||
			agreement.enrollingConditionSets.flatMap(
				(enrolling) =>
					(enrolling as unknown as EnrollingConditionSet).scopes ||
					(enrolling as GetAgreementDetails_baseAgreements_enrollingConditionSets).scopesConnection.edges.map((edge) => edge.node)
			)
		).flatMap((scope) => scope.treatmentSets.map((treatmentSet) => treatmentSet.preparations as Array<Preparation>))
	);
}

export function getTreatments(agreement: BaseAgreementDataType): Array<Treatment> {
	return selectMany(
		(
			(agreement as GetAgreementDetails_baseAgreements).enrollingConditionSets
				.flatMap((conditionSet) => conditionSet.scopesConnection.edges)
				.map((edge) => edge.node) ||
			(agreement.enrollingConditionSets as unknown as Array<EnrollingConditionSet>).flatMap((enrolling) => enrolling.scopes)
		).flatMap((scope) => scope.treatmentSets.map((treatmentSet) => treatmentSet.treatments as Array<Treatment>))
	);
}

export function getDiseases(
	agreement:
		| GetCaseByIdQuery["cases"][number]["baseAgreement"]
		| GetRefundSourceConditionsQuery["baseAgreements"][number]
		| GetAgreementDetails_baseAgreements
): Array<Disease> {
	const scopes = agreement.enrollingConditionSets
		.flatMap((conditionSet) => conditionSet.scopesConnection.edges as unknown as Array<EnrollingConditionSetScopesRelationship>)
		.map((edge) => edge.node);
	return scopes.flatMap((scope) => scope.diseases);
}

export function getIndications(
	agreement:
		| GetCaseByIdQuery["cases"][number]["baseAgreement"]
		| GetRefundSourceConditionsQuery["baseAgreements"][number]
		| GetAgreementDetails_baseAgreements
): Array<Indication> {
	const scopes = agreement.enrollingConditionSets
		.flatMap((conditionSet) => conditionSet.scopesConnection.edges as unknown as Array<EnrollingConditionSetScopesRelationship>)
		.map((edge) => edge.node);
	return scopes.flatMap((scope) => scope.indications);
}

export function formatEligibility(
	eligibility: GetRefundSourceConditionsQuery["baseAgreements"][number]["eligibilityConditionSetsConnection"]["edges"][number]
) {
	if (eligibility.node.code === EligibilityConditionCode.CYCLE_ADHERENCE) return `${eligibility.cycleAdherence || ""}`;
	return "";
}

export function formatPreparation(preparation: Preparation) {
	return preparation.BrandName;
}

export function formatDisease(disease: Disease) {
	return `${disease.Icd10Code} ${disease.Name}`;
}

export function formatTreatment(treatment: Pick<Treatment, "regulatedBy" | "Name" | "country">) {
	if (!treatment) {
		return "XX";
	}

	return treatment.country?.name === CountryName.Switzerland
		? `${treatment?.regulatedBy.BrandName || ""} ${treatment?.regulatedBy.DescriptionDe || ""} ${treatment?.Name || ""}`
		: `${treatment?.regulatedBy.BrandName || ""} ${treatment?.Name || ""}`;
}

type CurrencyTypes =
	| NonNullable<GetCaseByIdQuery["cases"][number]["baseAgreement"]>["currency"]
	| GetAgreementDetails_baseAgreements_currency
	| GetRefundSourceConditionsQuery["baseAgreements"][number]["currency"]
	| null;

function formatConditionBody(
	condition:
		| ScenarioCondition
		| NonNullable<GetCaseByIdQuery["cases"][number]["baseAgreement"]>["scenarios"][number]["conditions"][number]
		| null,
	t: (key: string) => any,
	fromFormatCondition?: boolean,
	dataContainers?:
		| Array<GetRefundSourceConditionsQuery["baseAgreements"][number]["dataContainers"][number]>
		| Array<GetAgreementDetails_baseAgreements_dataContainers>
		| Array<GetCaseByIdQuery["cases"][number]["baseAgreement"]["dataContainers"][number]>
		| null,
	currency?: CurrencyTypes
) {
	const items: string[] = [!fromFormatCondition ? t(`condition-type-${condition?.conditionType}`) : ""];
	const isAdministrationBased = !!(
		dataContainers as Array<NonNullable<GetCaseByIdQuery["cases"][number]["baseAgreement"]>["dataContainers"][number]>
	)?.find((dataContainer) => dataContainer.timeframeStartingPoint === TimeframeStartingPoint.FirstAdministrationDate);
	if (
		condition?.conditionType === ConditionType.Rating ||
		condition?.conditionType === ConditionType.Article71Status ||
		condition?.conditionType === ConditionType.Patent
	) {
		items.push(t("is"));
	}

	if (condition?.article71Status) {
		items.push(t(`article-71-status-${condition.article71Status}`));
	}

	if (condition?.patentStatus) {
		items.push(t(`patent-status-${condition.patentStatus}`));
	}

	if (condition?.brands?.length || condition?.treatments?.length || condition?.generics?.length) {
		items.push(
			brandOrTreatmentOrGenericDisplay([
				...((condition?.brands as Array<Brand>) || []),
				...((condition?.treatments as Array<Treatment>) || []),
				...((condition?.generics as Array<Generic>) || []),
			])
		);
	}

	if (condition?.conditionType === ConditionType.AmountOf) {
		items.push(t("based-on"));
		items.push(
			condition?.amountOfThresholdBasis === AmountOfThresholdBasis.GrossAmount
				? t("gross-amount").toLowerCase()
				: t("net-amount").toLowerCase()
		);
	}

	if (condition?.conditionType === ConditionType.PrescriptionNumber) {
		items.push(t(isAdministrationBased ? "administered" : "prescribed"));
	}

	if (condition?.treatmentDurationAspect) {
		items.push(t(`treatment-duration-condition-aspect-${condition?.treatmentDurationAspect}`));
	}
	if (condition?.conditionType === ConditionType.AdverseEvent) {
		items.push(t(`which-is`));
		items.push("/");
		items.push(condition.conditionStatus?.map((x) => t(`condition-status-${x}`)).join("*") || "");
		items.push("/");
		condition.grades && items.push(condition.grades?.length > 1 ? t("grades") : t("grade"));
		items.push("/");
		items.push(condition.grades?.join("*") || t("any"));
		items.push("/");
		items.push(condition.adverseEvents?.map((ae) => t(ae)).join("*") || t("any"));
		items.push("/");
		items.push(t("occurs"));
		items.push(t(condition.occurence || ""));
	}

	if (condition?.conditionType === ConditionType.Response) {
		items.push(t(`response-inclusion-type-${condition.responseInclusionType}`));
		items.push(t(condition.responseType || ""));
	}

	if (condition?.conditionType === ConditionType.Event) {
		items.push(condition.eventSubCategories?.map((subCategory) => t(subCategory)).join(', ') || t("event-any"));
		if (condition.eventType) {
			items.push(t(`event-type-${condition.eventType}`));
		}
	}

	if (condition?.treatmentDurationAspect === TreatmentDurationConditionAspect.Discontinued) {
		items.push(t("due-to"));
	}
	if (condition?.discontinuationReason) {
		items.push(t(`treatment-discontinuation-reason-${condition?.discontinuationReason}`));
	}
	if (condition?.patientScope) {
		items.push(t(`prescription-number-scope-${condition?.patientScope}`));
	}
	if (condition?.monitoringType) {
		items.push(t(`monitoring-type-${condition?.monitoringType}`));
	}
	if (condition?.countdownPoint) {
		items.push(t(`countdown-point-${condition?.countdownPoint}`));
		items.push(t("test-result"));
	}
	if (
		condition?.timeframeRangeType &&
		condition?.conditionType !== ConditionType.Rating &&
		condition?.conditionType !== ConditionType.Article71Status &&
		condition.conditionType !== ConditionType.Patent
	) {
		items.push(t(`timeframe-range-type-${condition?.timeframeRangeType}`));
	}
	if (condition?.consideredRating) {
		items.push(t(condition?.consideredRating));
	}
	if (condition?.rollingRangeType) {
		items.push(t(`rolling-range-type-${condition?.rollingRangeType}`));
	}
	if (!isEmptyValue(condition?.timeframeStart) || !isEmptyValue(condition?.timeframeEnd)) {
		items.push([condition?.timeframeStart, condition?.timeframeEnd].filter((v) => !isEmptyValue(v)).join("-"));
		items.push(t("days"));
	}

	if (condition?.startingPoint) {
		items.push(t(`timeframe-relation-${condition.timeframeRelation}`));
		items.push(t(`timeframe-starting-point-${condition?.startingPoint}`));
	}

	if (condition?.monitoringType === MonitoringType.ChangedBetween) {
		items.push(t("and"));
	}

	if (condition?.comparedCountdownPoint) {
		items.push(t(`countdown-point-${condition?.comparedCountdownPoint}`));
		items.push(t("test-result"));
	}

	if (condition?.comparedTimeframeRangeType) {
		items.push(t(`timeframe-range-type-${condition?.comparedTimeframeRangeType}`));
	}

	if (!isEmptyValue(condition?.comparedTimeframeStart)) {
		items.push([condition?.comparedTimeframeStart, condition?.comparedTimeframeEnd].filter((v) => !isEmptyValue(v)).join("-"));
		items.push(t("days"));
	}

	if (condition?.comparedStartingPoint) {
		items.push(t(`timeframe-relation-${condition.comparedTimeframeRelation}`));
		items.push(t(`timeframe-starting-point-${condition?.comparedStartingPoint}`));
	}

	if (condition?.deltaType) {
		items.push(t("and"));
		items.push(t(`delta-type-${condition?.deltaType}`));
		items.push(t("delta"));
	}

	if (condition?.operator) {
		items.push(t(`arithmetical-operator-${condition?.operator}`));
	}
	if (condition?.bothOperatorFirst) {
		items.push(t(`arithmetical-operator-${condition?.bothOperatorFirst}`));
	}
	if (!isEmptyValue(condition?.value)) {
		items.push(`${formatNumber(condition?.value)} ${condition?.conditionType === ConditionType.SecondaryInterventionRate ? "%" : ""}`);
	}
	if (condition?.bothOperatorSecond) {
		items.push(`and `);
		items.push(t(`arithmetical-operator-${condition?.bothOperatorSecond}`));
	}
	if (!isEmptyValue(condition?.endRangeValue)) {
		items.push(`${condition?.endRangeValue}`);
	}
	if (condition?.uoMType && condition?.uoMType !== UoMType.Cur) {
		items.push(t(`uom-type-${condition?.uoMType}`));
	}

	if (condition?.uoMType && condition?.uoMType === UoMType.Cur) {
		items.push(currency?.Code ?? "");
	}
	if (condition?.conditionType === ConditionType.LdlLevel) {
		items.push("%");
	}

	return items.join(" ");
}

export function formatCondition(
	condition:
		| ScenarioCondition
		| GetAgreementDetails_baseAgreements["scenarios"][number]["conditions"][number]
		| GetRefundSourceConditionsQuery["baseAgreements"][number]["scenarios"][number]["conditions"][number]
		| NonNullable<GetCaseByIdQuery["cases"][number]["baseAgreement"]>["scenarios"][number]["conditions"][number]
		| GetCaseByIdQuery["cases"][number]["baseAgreement"]["scenarios"][number]["conditions"][number],
	t: (key: string) => any,
	dataContainers?:
		| Array<GetRefundSourceConditionsQuery["baseAgreements"][number]["dataContainers"][number]>
		| Array<GetAgreementDetails_baseAgreements_dataContainers>
		| Array<GetCaseByIdQuery["cases"][number]["baseAgreement"]["dataContainers"][number]>
		| null,
	currency?:
		| NonNullable<GetCaseByIdQuery["cases"][number]["baseAgreement"]>["currency"]
		| GetAgreementDetails_baseAgreements_currency
		| GetRefundSourceConditionsQuery["baseAgreements"][number]["currency"]
		| null
) {
	const conditionType =
		condition.conditionType === ConditionType.Event
			? t(condition.eventCategory || "")
			: t(`condition-type-${condition?.conditionType}`);

	return `${conditionType}/${formatConditionBody(condition as ScenarioCondition, t, true, dataContainers, currency)}`;
}

export function formatICondition(
	condition:
		| ScenarioCondition
		| NonNullable<GetCaseByIdQuery["cases"][number]["baseAgreement"]>["scenarios"][number]["conditions"][number],
	t: (key: string) => any,
	dataContainers:
		| Array<GetCaseByIdQuery["cases"][number]["baseAgreement"]["dataContainers"][number]>
		| GetRefundSourceConditionsQuery["baseAgreements"][number]["dataContainers"],
	currency?:
		| NonNullable<GetCaseByIdQuery["cases"][number]["baseAgreement"]>["currency"]
		| GetAgreementDetails_baseAgreements_currency
		| GetRefundSourceConditionsQuery["baseAgreements"][number]["currency"]
		| null
) {
	const conditionName =
		condition.conditionType === ConditionType.Event
			? t(condition.eventCategory || "")
			: t(`condition-type-${condition?.conditionType}`);

	return {
		name: conditionName,
		value: formatConditionBody(condition, t, true, dataContainers, currency),
		conditionType: ConditionArea.CONCLUSIVE,
	};
}

export function formatAction(
	action:
		| ScenarioAction
		| NonNullable<GetCaseByIdQuery["cases"][number]["baseAgreement"]>["scenarios"][number]["actions"][number]
		| GetAgreementDetails_baseAgreements_scenarios_actions,
	agreement: BaseAgreement | NonNullable<GetCaseByIdQuery["cases"][number]["baseAgreement"]>,
	relevantVolumeBasedCondition: ScenarioCondition,
	t: (key: string) => any,
	dataContainers?:
		| GetRefundSourceConditionsQuery["baseAgreements"][number]["dataContainers"]
		| Array<DataContainer>
		| NonNullable<GetCaseByIdQuery["cases"][number]["baseAgreement"]>["dataContainers"]
		| null
) {
	const items = [];
	items.push(t(`action-type-${action?.actionType}`));
	if (!isEmptyValue(action?.value)) {
		items.push(`${formatNumber(action?.value)}`);
	}

	if (action.isAdHocDiscount) {
		items.push(t("ad-hoc"));
	}

	if (!isEmptyValue(action?.priceType)) {
		if (isFixedPrice(action?.priceType)) {
			items.push(agreement.currency?.Code);
		}
		items.push(t(`price-type-${action?.priceType}`));
	}

	if (action.adHocPriceTypes?.length) {
		items.push(
			action.adHocPriceTypes
				.map((priceType) => {
					const priceTypeText = t(`price-type-${priceType}`);
					return isFixedPrice(priceType) ? `${agreement.currency?.Code} ${priceTypeText}` : priceTypeText;
				})
				.join(", ")
		);
	}

	if (action?.entityPriceType) {
		items.push(t("per"));
		items.push(t(`entity-price-type-${action?.entityPriceType}`));
	}
	if (action?.entityPriceType !== EntityPriceType.Case) {
		items.push(t("to"));
		items.push(t(`item-scope-${action?.itemScope}`));
		if (action?.itemRangeType) {
			items.push(t(`action-item-range-type-${action?.itemRangeType}`));
		}
		if (!isEmptyValue(action?.startItemNumber)) {
			items.push([action?.startItemNumber, action?.endItemNumber].filter((x) => !isEmptyValue(x)).join("-"));
		}

		if (action?.entityPriceType !== EntityPriceType.Cycle)
			items.push((agreement as BaseAgreement).subjectMatter === AgreementSubjectMatter.Devices ? t("items-of") : t("packages-of"));
		else {
			items.push(t("cycles-of"));
			items.push(action.cycle?.name || "");
		}

		if (action?.packOptionType) {
			items.push(t(`pack-option-type-${action?.packOptionType}`));
		}

		if (action?.ingredientRangeType) {
			items.push(t(`ingredient-range-type-${action?.ingredientRangeType}`));
		}

		if (!isEmptyValue(action?.ingredientStartItemNumber)) {
			items.push([action?.ingredientStartItemNumber, action?.ingredientEndItemNumber].filter((x) => !isEmptyValue(x)).join("-"));
		}

		if (action?.uoMType && action?.uoMType !== UoMType.Cur) {
			items.push(t(`uom-type-${action?.uoMType}`));
			items.push(t("active-ingredient-quantity-of"));
		}

		if (action?.uoMType && action?.uoMType === UoMType.Cur) {
			items.push(agreement.currency?.Code);
			items.push(
				t(
					relevantVolumeBasedCondition &&
						relevantVolumeBasedCondition.amountOfThresholdBasis === AmountOfThresholdBasis.NetAmount &&
						action.ingredientRangeType &&
						conditionBoundRanges.includes(action.ingredientRangeType as any)
						? "net-amount"
						: "gross-amount"
				).toLowerCase()
			);
		}

		if (relevantVolumeBasedCondition && relevantVolumeBasedCondition.patientScope) {
			items.push(t(`prescription-number-scope-${relevantVolumeBasedCondition?.patientScope}`));
		}

		if (
			action?.brandsConnection?.edges.length ||
			action?.treatmentsConnection?.edges.length ||
			action?.genericsConnection?.edges.length
		) {
			items.push(
				brandOrTreatmentOrGenericDisplay([
					...(([...(action?.brandsConnection.edges || [])]
						.sort(sortBySelectors((orderConnection: any) => orderConnection.order || 0))
						.map((orderConnection) => orderConnection.node) as Array<Brand>) || []),
					...(([...(action?.treatmentsConnection.edges || [])]
						.sort(sortBySelectors((orderConnection: any) => orderConnection.order || 0))
						.map((orderConnection) => orderConnection.node) as Array<Treatment>) || []),
					...(([...(action?.genericsConnection.edges || [])]
						.sort(sortBySelectors((orderConnection: any) => orderConnection.order || 0))
						.map((orderConnection) => orderConnection.node) as Array<Generic>) || []),
				])
			);
		}

		if ((agreement as BaseAgreement).subjectMatter === AgreementSubjectMatter.Devices) {
			items.push(t("sold"));
		} else {
			items.push(t(`${(action as any).recordNature}`.toLowerCase()));
		}

		items.push(t(`timeframe-range-type-${action?.timeframeRangeType}`));
		if (action?.rollingRangeType) {
			items.push(t(`rolling-range-type-${action?.rollingRangeType}`));
		}
		if (!isEmptyValue(action?.timeframeStart) && action?.timeframeRangeType !== TimeframeRangeType.Ever) {
			items.push([action?.timeframeStart, action?.timeframeEnd].filter((v) => !isEmptyValue(v)).join("-"));
		}

		if (
			action?.timeframeRangeType !== TimeframeRangeType.Ever &&
			action?.timeframeRangeType !== TimeframeRangeType.WithinTheCoveragePeriod &&
			action?.timeframeRangeType !== TimeframeRangeType.WithinTherapyTrial &&
			!action?.ingredientRangeType &&
			(agreement as BaseAgreement).subjectMatter !== AgreementSubjectMatter.Devices
		) {
			items.push(t("days"));
		}

		if (action?.startingPoint) {
			items.push(
				t(
					action.timeframeRangeType === TimeframeRangeType.WithinTheLast ||
						action.startingPoint === TimeframeStartingPoint.DiscontinuationDate
						? `action-item-range-type-${TimeframeRelation.Before}`
						: "starting-from"
				)
			);
			items.push(t(`timeframe-starting-point-${action?.startingPoint}`));
		}

		if (!isEmptyValue(action?.itemCombinationRule)) {
			items.push(t(`action-item-combination-rule-${action?.itemCombinationRule}`));
		}

		if (action?.itemCombinationRule === ActionItemCombinationRule.InCombinationWith) {
			const isAdministrationBased = !!(dataContainers as Array<DataContainer>)?.find(
				(c) => c?.timeframeStartingPoint === TimeframeStartingPoint.FirstAdministrationDate
			);
			items.push(
				brandOrTreatmentOrGenericDisplay([
					...(action?.combinedItemBrands as Array<Brand>),
					...(action?.combinedItemTreatments as Array<Treatment>),
					...(action?.combinedItemGenerics as Array<Generic>),
				])
			);
			items.push(t(isAdministrationBased ? "administered" : "prescribed"));
			items.push(t(`timeframe-range-type-${action?.combinedItemTimeframeRangeType}`));
			items.push([action?.combinedItemTimeframeStart, action?.combinedItemTimeframeEnd].filter((v) => !isEmptyValue(v)).join("-"));
			items.push(t("days"));
			items.push(t(`action-item-combination-relation-${action?.itemCombinationRelation}`));
		}
	}

	return items.join(" ");
}

export function brandOrTreatmentOrGenericDisplay(entities: Array<Brand | Treatment | Generic> | null): string {
	if (!entities || !entities.filter(Boolean).length) return "";

	switch (entities[0].__typename) {
		case "Brand":
			return entities.map((brand) => (brand as Brand).Name).join(", ");

		case "Treatment":
			return entities.map((treatment) => formatTreatment(treatment as Treatment)).join(", ");

		case "Generic":
			return entities.map((generic) => (generic as Generic).Name).join(", ");

		default:
			return "";
	}
}

export function indicationDisplay(
	indication:
		| Pick<
				Indication,
				| "type"
				| "intent"
				| "line"
				| "mutation"
				| "typeLocalizationKey"
				| "intentLocalizationKey"
				| "lineLocalizationKey"
				| "mutationLocalizationKey"
		  >
		| undefined
		| null,
	translate: TrFun
) {
	return (
		indication &&
		[
			(indication.typeLocalizationKey &&
				(translate(indication.typeLocalizationKey) === indication.typeLocalizationKey
					? indication.type
					: translate(indication.typeLocalizationKey))) ||
				indication.type,
			(indication.intentLocalizationKey &&
				(translate(indication.intentLocalizationKey) === indication.intentLocalizationKey
					? indication.intent
					: translate(indication.intentLocalizationKey))) ||
				indication.intent,
			(indication.lineLocalizationKey &&
				(translate(indication.lineLocalizationKey) === indication.lineLocalizationKey
					? indication.line
					: translate(indication.lineLocalizationKey))) ||
				indication.line,
			(indication.mutationLocalizationKey &&
				(translate(indication.mutationLocalizationKey) === indication.mutationLocalizationKey
					? indication.mutation
					: translate(indication.mutationLocalizationKey))) ||
				indication.mutation,
		]
			.filter(Boolean)
			.join(" - ")
	);
}

export function relevantIndicationDisplay(
	indication:
		| Pick<Article71Indication, "mainIndication" | "approvalDate" | "countryScopeId" | "id" | "sllAdditionDate" | "name">
		| undefined
		| null
) {
	return indication && `${indication.name}${indication.countryScopeId ? " (" + indication.countryScopeId + ")" : ""}`;
}
